/**
 * Hook para iniciar sync Flexy y hacer polling de progreso
 */
import { useState, useCallback, useRef, useEffect } from 'react'
import toast from 'react-hot-toast'
import { FlexyStockService } from '../../services/cron/flexyStockService'
import { FLEXY_STOCK_STATUS_POLL_MS } from '../../constants/flexyStock'
import {
  buildFlexySyncProgress,
  readFlexyApiErrorMessage,
} from '../../utils/flexyStockUtils'
import type {
  FlexyStockSyncPhase,
  FlexyStockSyncProgress,
  FlexySyncAllJob,
  FlexyWarehouseResult,
} from '../../types/cron/flexyStock'

export type FlexySyncMode = 'all' | 'warehouse'

interface UseFlexyStockSyncReturn {
  phase: FlexyStockSyncPhase
  progress: FlexyStockSyncProgress
  job: FlexySyncAllJob | null
  warehouseResult: FlexyWarehouseResult | null
  error: string | null
  isProcessing: boolean
  startAllSync: () => Promise<void>
  startWarehouseSync: (
    idalmacen: number,
    warehouse?: { codigo?: string; nombre?: string }
  ) => Promise<void>
  attachToRunningJob: () => Promise<void>
  reset: () => void
}

const INITIAL_PROGRESS: FlexyStockSyncProgress = {
  completedCount: 0,
  totalWarehouses: 0,
  currentWarehouseId: null,
  currentWarehouseName: null,
  percent: 0,
}

export function useFlexyStockSync(): UseFlexyStockSyncReturn {
  const [phase, setPhase] = useState<FlexyStockSyncPhase>('idle')
  const [progress, setProgress] = useState<FlexyStockSyncProgress>(INITIAL_PROGRESS)
  const [job, setJob] = useState<FlexySyncAllJob | null>(null)
  const [warehouseResult, setWarehouseResult] = useState<FlexyWarehouseResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const mountedRef = useRef(true)

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])

  const applyJob = useCallback((nextJob: FlexySyncAllJob | undefined) => {
    if (!nextJob) return
    setJob(nextJob)
    setProgress(buildFlexySyncProgress(nextJob))
  }, [])

  const finishFromJob = useCallback(
    (nextJob: FlexySyncAllJob) => {
      stopPolling()
      if (nextJob.status === 'completed') {
        setPhase('completed')
        toast.success('Sincronización de stock Flexy completada', { duration: 4000 })
        return
      }
      if (nextJob.status === 'failed') {
        setPhase('error')
        setError(nextJob.error ?? 'La sincronización falló')
        toast.error(nextJob.error ?? 'La sincronización falló', { duration: 5000 })
      }
    },
    [stopPolling]
  )

  const pollOnce = useCallback(async () => {
    try {
      const status = await FlexyStockService.getStatus()
      if (!mountedRef.current) return

      const syncJob = status.sync_all_job
      if (!syncJob) return

      applyJob(syncJob)

      if (!syncJob.running) {
        finishFromJob(syncJob)
      }
    } catch (err) {
      if (!mountedRef.current) return
      stopPolling()
      const message = readFlexyApiErrorMessage(err)
      setError(message)
      setPhase('error')
    }
  }, [applyJob, finishFromJob, stopPolling])

  const startPolling = useCallback(() => {
    stopPolling()
    setPhase('polling')
    pollOnce()
    pollRef.current = setInterval(pollOnce, FLEXY_STOCK_STATUS_POLL_MS)
  }, [pollOnce, stopPolling])

  const startAllSync = useCallback(async () => {
    setError(null)
    setWarehouseResult(null)
    setPhase('starting')

    try {
      const response = await FlexyStockService.startAllWarehousesSync()
      if (!mountedRef.current) return

      if (response.job) {
        applyJob(response.job)
      }

      toast.success(
        response.message ?? 'Sincronización multi-bodega iniciada',
        { duration: 4000 }
      )
      startPolling()
    } catch (err: unknown) {
      if (!mountedRef.current) return

      const alreadyRunning =
        err &&
        typeof err === 'object' &&
        'isAlreadyRunning' in err &&
        (err as { isAlreadyRunning?: boolean }).isAlreadyRunning

      if (alreadyRunning) {
        toast('Ya hay una sincronización en curso. Mostrando progreso...', {
          icon: '⏳',
        })
        startPolling()
        return
      }

      const message = readFlexyApiErrorMessage(err)
      setError(message)
      setPhase('error')
      toast.error(message, { duration: 5000 })
    }
  }, [applyJob, startPolling])

  const attachToRunningJob = useCallback(async () => {
    setError(null)
    setWarehouseResult(null)
    setPhase('starting')

    try {
      const status = await FlexyStockService.getStatus()
      if (!mountedRef.current) return

      const syncJob = status.sync_all_job
      if (syncJob?.running) {
        applyJob(syncJob)
        startPolling()
        return
      }

      if (syncJob?.status === 'completed' || syncJob?.status === 'failed') {
        applyJob(syncJob)
        finishFromJob(syncJob)
        return
      }

      setPhase('idle')
    } catch (err) {
      if (!mountedRef.current) return
      setError(readFlexyApiErrorMessage(err))
      setPhase('error')
    }
  }, [applyJob, finishFromJob, startPolling])

  const startWarehouseSync = useCallback(async (
    idalmacen: number,
    warehouse?: { codigo?: string; nombre?: string }
  ) => {
    setError(null)
    setJob(null)
    setPhase('starting')

    try {
      const response = await FlexyStockService.syncSingleWarehouse(idalmacen)
      if (!mountedRef.current) return

      const result: FlexyWarehouseResult = {
        idalmacen,
        codigo: warehouse?.codigo ?? '',
        nombre: warehouse?.nombre ?? `Bodega ${idalmacen}`,
        success: response.success,
        items: response.data?.items,
        warehouse_duration_ms: response.data?.warehouse_duration_ms,
      }

      setWarehouseResult(result)
      setPhase('completed')
      toast.success(response.message ?? 'Bodega sincronizada correctamente', {
        duration: 4000,
      })
    } catch (err) {
      if (!mountedRef.current) return
      const message = readFlexyApiErrorMessage(err)
      setError(message)
      setPhase('error')
      toast.error(message, { duration: 5000 })
    }
  }, [])

  const reset = useCallback(() => {
    stopPolling()
    setPhase('idle')
    setProgress(INITIAL_PROGRESS)
    setJob(null)
    setWarehouseResult(null)
    setError(null)
  }, [stopPolling])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      stopPolling()
    }
  }, [stopPolling])

  const isProcessing =
    phase === 'starting' || phase === 'polling'

  return {
    phase,
    progress,
    job,
    warehouseResult,
    error,
    isProcessing,
    startAllSync,
    startWarehouseSync,
    attachToRunningJob,
    reset,
  }
}
